import {
    Component,
    computed,
    ElementRef,
    HostListener,
    OnDestroy,
    signal,
    Signal,
    viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { LucideBuilding2, LucideChevronDown, LucideSearch } from '@lucide/angular';
import { AppService } from '../../services/app.service';
import { AppContext } from '../../services/app-context.service';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { isTotalEstablishment, TOTAL_ESTABLISHMENT } from '../../services/establishment-scope';
import {
    buildLocationTree,
    filterLocationNodes,
    findByTypeAhead,
    LocationNode,
    SEARCH_THRESHOLD,
} from './location-hierarchy';

interface Establishment {
    id: string;
    name: string;
    parentEstablishmentId?: string | null;
}

/**
 * The vestigingswisselaar: the app's main context switch.
 *
 * Styled as a distinct blue control rather than a form field, because switching it changes what
 * every other screen is about — it is not a value you fill in. Shows the franchise hierarchy so
 * it is clear which region a vestiging sits under, and confirms the switch with a toast.
 *
 * A user with access to a single vestiging gets the name and icon without a chevron and without
 * interaction: there is nothing to switch to.
 */
@Component({
    selector: 'app-location-switcher',
    imports: [FormsModule, IconComponent],
    templateUrl: './location-switcher.component.html',
})
export class LocationSwitcherComponent implements OnDestroy {
    protected readonly buildingIcon = LucideBuilding2;
    protected readonly chevronIcon = LucideChevronDown;
    protected readonly searchIcon = LucideSearch;

    private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

    protected readonly open = signal(false);
    protected readonly search = signal('');
    protected readonly activeIndex = signal(0);

    private typeAheadBuffer = '';
    private typeAheadTimeout?: ReturnType<typeof setTimeout>;

    private readonly establishments: Signal<Establishment[]> = toSignal(
        this.appService.accessibleEstablishments$.pipe(
            map(list => (list ?? []) as Establishment[]),
        ),
        { initialValue: [] },
    );

    private readonly selectedId = toSignal(this.appContext.establishmentId$, { initialValue: null });

    /**
     * Reading across every vestiging at once. Owners and admins only — for anyone else a combined
     * figure would either leak the vestigingen they cannot see or quietly mean something different
     * per person.
     */
    private readonly canSeeTotal = toSignal(this.appService.isAdminOrOwner$, { initialValue: false });

    /**
     * Every accessible vestiging, in franchise order, merged ones removed — with Totaal at the top
     * for those who may see it.
     *
     * It joins the same list rather than sitting above it, so search, arrow keys and type-ahead
     * reach it without a second code path. The list is what the keyboard walks.
     */
    protected readonly allNodes = computed<LocationNode<Establishment>[]>(() => {
        const tree = buildLocationTree(this.establishments());

        if (!this.canSeeTotal() || tree.length < 2) {
            return tree;
        }

        return [
            { establishment: TOTAL_ESTABLISHMENT as Establishment, depth: 0, isParent: false },
            ...tree,
        ];
    });

    /** What the dropdown currently shows — the same list, narrowed by the search field. */
    protected readonly visibleNodes = computed(() => filterLocationNodes(this.allNodes(), this.search()));

    protected readonly showSearch = computed(() => this.allNodes().length > SEARCH_THRESHOLD);

    protected readonly isInteractive = computed(() => this.allNodes().length > 1);

    protected readonly current = computed(() => {
        const id = this.selectedId();
        const nodes = this.allNodes();
        return nodes.find(node => node.establishment.id === id)?.establishment
            ?? nodes[0]?.establishment
            ?? null;
    });

    constructor(
        private readonly appService: AppService,
        private readonly appContext: AppContext,
        private readonly router: Router,
        private readonly messageService: MessageService,
        private readonly elementRef: ElementRef<HTMLElement>,
    ) {
        // The redirect lives in select() rather than on the establishmentId stream. Landing on the
        // dashboard is right when *this* control switches context — the page you were on would
        // otherwise show another vestiging's data. But the context also changes when you open a
        // record from the totals view, and there the whole point is to land on that record.
        //
        // Watching the stream could not tell those apart and sent both to the dashboard.
    }

    ngOnDestroy() {
        clearTimeout(this.typeAheadTimeout);
    }

    @HostListener('document:click', ['$event'])
    protected onDocumentClick(event: MouseEvent) {
        if (this.open() && !this.elementRef.nativeElement.contains(event.target as Node)) {
            this.close();
        }
    }

    protected toggle() {
        if (!this.isInteractive()) {
            return;
        }

        if (this.open()) {
            this.close();
        } else {
            this.openList();
        }
    }

    protected openList() {
        this.open.set(true);
        this.search.set('');

        const currentId = this.current()?.id;
        const index = this.visibleNodes().findIndex(node => node.establishment.id === currentId);
        this.activeIndex.set(index >= 0 ? index : 0);

        if (this.showSearch()) {
            // Defer until the field exists in the DOM.
            setTimeout(() => this.searchInput()?.nativeElement.focus());
        }
    }

    protected close() {
        this.open.set(false);
        this.search.set('');
        this.typeAheadBuffer = '';
    }

    protected onSearchChange(value: string) {
        this.search.set(value);
        this.activeIndex.set(0);
    }

    protected select(node: LocationNode<Establishment>) {
        const establishment = node.establishment;
        this.close();

        if (establishment.id === this.selectedId()) {
            return;
        }

        this.appContext.set('establishmentId', establishment.id);
        this.router.navigate(['establishment', 'dashboard']);

        this.messageService.add({
            severity: 'success',
            summary: isTotalEstablishment(establishment.id) ? 'Totaaloverzicht' : 'Vestiging gewijzigd',
            detail: isTotalEstablishment(establishment.id)
                ? 'Je ziet nu alle vestigingen samen. Wijzigen kan alleen binnen één vestiging.'
                : `Overgeschakeld naar ${establishment.name}`,
            life: 3000,
        });
    }

    protected isSelected(node: LocationNode<Establishment>): boolean {
        return node.establishment.id === this.current()?.id;
    }

    /** Indent one step per franchise level, so a sub-vestiging reads as sitting under its region. */
    protected indentPx(depth: number): number {
        return depth * 16;
    }

    protected optionId(index: number): string {
        return `location-option-${index}`;
    }

    // ----------[ Keyboard ]----------

    protected onTriggerKeydown(event: KeyboardEvent) {
        if (!this.isInteractive()) {
            return;
        }

        // Once open without a search field the trigger keeps focus, so it also drives the list.
        if (this.open()) {
            this.onListKeydown(event);
            return;
        }

        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowUp':
            case 'Enter':
            case ' ':
                event.preventDefault();
                this.openList();
                return;
            case 'Escape':
                this.close();
                return;
        }
    }

    protected onListKeydown(event: KeyboardEvent) {
        const nodes = this.visibleNodes();

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.moveActive(1);
                return;
            case 'ArrowUp':
                event.preventDefault();
                this.moveActive(-1);
                return;
            case 'Home':
                event.preventDefault();
                this.activeIndex.set(0);
                return;
            case 'End':
                event.preventDefault();
                this.activeIndex.set(Math.max(0, nodes.length - 1));
                return;
            case 'Enter':
                event.preventDefault();
                if (nodes[this.activeIndex()]) {
                    this.select(nodes[this.activeIndex()]);
                }
                return;
            case 'Escape':
            case 'Tab':
                this.close();
                return;
        }

        // Type-ahead: jump to the first vestiging starting with what was typed. Skipped when the
        // search field is present — there the same keystrokes belong in the field.
        if (!this.showSearch() && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            this.handleTypeAhead(event.key, nodes);
        }
    }

    private handleTypeAhead(key: string, nodes: readonly LocationNode<Establishment>[]) {
        this.typeAheadBuffer += key.toLowerCase();
        clearTimeout(this.typeAheadTimeout);
        this.typeAheadTimeout = setTimeout(() => (this.typeAheadBuffer = ''), 700);

        const index = findByTypeAhead(nodes, this.typeAheadBuffer, this.activeIndex());
        if (index >= 0) {
            this.activeIndex.set(index);
        }
    }

    private moveActive(delta: number) {
        const count = this.visibleNodes().length;
        if (count === 0) {
            return;
        }

        this.activeIndex.set((this.activeIndex() + delta + count) % count);
    }
}
